import { useEffect, useMemo, useState } from "react";

import { Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { usePostHog } from "posthog-js/react";

import { RefreshPageButton } from "components/RefreshPageButton";
import { useAPI, useRoleContext } from "hooks";
import { extractAPIRequestErrorMessage } from "utils";

import { ClientAppEvent } from "../../../constants";
import { CreateFollowRequest } from "../CreateFollowRequest";
import { ShipperNetworkTable } from "./ShipperNetworkTable";
import { ShipperPendingRequests } from "./ShipperPendingRequests";

import type { MyCarrierItem } from "./types";

interface Props {
  networkGroupsComponent: React.ReactNode;
}

export function ShipperSentRequestsContainer(props: Props) {
  const { networkGroupsComponent } = props;
  const { role } = useRoleContext();
  const { api } = useAPI();
  const posthog = usePostHog();

  const [error, setError] = useState<string | null>(null);
  const [loadingPendingRequests, setLoadingPendingRequests] =
    useState<boolean>(true);
  const [followersError, setFollowersError] = useState<string | null>(null);
  const [followersLoading, setFollowersLoading] = useState<boolean>(true);
  const [followers, setFollowers] = useState<FollowingCarrier[]>([]);
  const [pendingRequests, setPendingRequests] = useState<
    FollowShipperRequestAndResponse[]
  >([]);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removingKey, setRemovingKey] = useState<string | null>(null);

  useEffect(() => {
    if (loadingPendingRequests) {
      api<FollowShipperRequestAndResponse[]>(
        "get",
        role ? `/shippers/${role.id}/follow_requests/sent` : "/",
      )
        .then((response) => {
          // The endpoint only returns requests still awaiting a response.
          setPendingRequests(response.data);
          setError(null);
        })
        .catch((e) => {
          setError(
            e?.response?.data?.message ??
              "Failed to fetch network information.",
          );
        })
        .finally(() => {
          setLoadingPendingRequests(false);
        });
    }
  }, [api, role, loadingPendingRequests]);

  useEffect(() => {
    if (followersLoading) {
      api<FollowingCarrier[]>(
        "get",
        role ? `/shippers/${role.id}/followers` : "/",
      )
        .then((response) => {
          setFollowers(response.data);
          setFollowersError(null);
        })
        .catch((e) => {
          setFollowersError(
            e?.response?.data?.message ?? "Failed to fetch carrier network.",
          );
        })
        .finally(() => {
          setFollowersLoading(false);
        });
    }
  }, [api, role, followersLoading]);

  const refreshAll = () => {
    setFollowersLoading(true);
    setLoadingPendingRequests(true);
  };

  // Email invites (external) are already in the network and get the shipper's
  // loads, so they belong in "My Carriers". Only add-partner invites to
  // existing Evotrux carriers awaiting acceptance are genuinely "Pending".
  const externalInvites = useMemo(
    () =>
      pendingRequests.filter(
        (r) => r.request.type === "SHIPPER_FOLLOW_REQUEST_EXTERNAL",
      ),
    [pendingRequests],
  );
  const partnerInvites = useMemo(
    () =>
      pendingRequests.filter(
        (r) => r.request.type === "SHIPPER_FOLLOW_REQUEST",
      ),
    [pendingRequests],
  );

  const myCarriers: MyCarrierItem[] = useMemo(
    () => [
      ...followers.map((c) => ({
        key: `m-${c.id}`,
        kind: "member" as const,
        user: {
          first_name: c.user.first_name,
          last_name: c.user.last_name,
        },
        company: c.company,
        created_at: c.created_at,
        removeId: c.id,
      })),
      ...externalInvites.map((r) => ({
        key: `i-${r.request.id}`,
        kind: "invite" as const,
        // An external invite always carries the email it was sent to; the
        // fallback only satisfies the nullable type.
        user: { first_name: r.request.target_email ?? "", last_name: "" },
        company: null,
        created_at: r.request.created_at,
        removeId: r.request.id,
      })),
    ],
    [followers, externalInvites],
  );

  const onRemove = (item: MyCarrierItem) => {
    if (role == null || removingKey != null) {
      return;
    }
    setRemoveError(null);
    setRemovingKey(item.key);
    const request =
      item.kind === "member"
        ? api(
            "post",
            `/shippers/${role.id}/followers/${item.removeId}/delete`,
            {},
          )
        : api("delete", `/follow_requests/${item.removeId}`);
    request
      .then(() => {
        posthog?.capture(ClientAppEvent.SHIPPER_NETWORK_CARRIER_REMOVED, {
          kind: item.kind,
          removed_id: item.removeId,
        });
        // A member removal only affects followers; an invite removal only
        // affects pending requests. Refetch just the list that changed.
        if (item.kind === "member") {
          setFollowersLoading(true);
        } else {
          setLoadingPendingRequests(true);
        }
      })
      .catch((e) => {
        setRemoveError(
          extractAPIRequestErrorMessage(e, "Failed to remove carrier."),
        );
      })
      .finally(() => {
        setRemovingKey(null);
      });
  };

  const showPending = !loadingPendingRequests && partnerInvites.length > 0;

  return (
    <>
      <CreateFollowRequest onFollowRequestsCreatedRefresh={refreshAll} />
      <Box flex={1} mt="1.5rem">
        <Stack direction="row" alignItems="center">
          <Typography variant="h6">My Carriers</Typography>
          <RefreshPageButton
            id="refresh-my-carriers"
            onRefresh={refreshAll}
            iconButton
          />
        </Stack>
        {removeError != null && (
          <Typography color="red" mt=".5rem">
            {removeError}
          </Typography>
        )}
        <Box mt="1rem">
          <ShipperNetworkTable
            loading={followersLoading || loadingPendingRequests}
            items={myCarriers}
            error={followersError ?? error}
            onRemove={onRemove}
            removingKey={removingKey}
          />
        </Box>
      </Box>
      {showPending && (
        <Box flex={1} mt="1.5rem">
          <Stack direction="row" alignItems="center">
            <Typography variant="h6">Pending Invites</Typography>
            <RefreshPageButton
              id="refresh-pending"
              onRefresh={() => setLoadingPendingRequests(true)}
              iconButton
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" mt=".25rem">
            Carriers you invited who haven&apos;t accepted yet.
          </Typography>
          <Box mt="1rem">
            <ShipperPendingRequests
              error={error}
              loading={loadingPendingRequests}
              pendingRequests={partnerInvites}
              onRefetchFollowers={() => setLoadingPendingRequests(true)}
            />
          </Box>
        </Box>
      )}
      <Box mt="2.5rem" pb="1rem">
        {networkGroupsComponent}
      </Box>
    </>
  );
}
